"use client";

import React, { useEffect, useMemo, useState, useRef } from "react";

import { COUNTRIES } from "../countries";
import styles from "./style.module.scss";

// ─── Helpers ─────────────────────────────────────────────────────────────────
function findBestCountryCode(fullValue?: string) {
  if (!fullValue) return null;
  const codes = Array.from(new Set(COUNTRIES.map((c) => c.code))).sort(
    (a, b) => b.length - a.length
  );
  for (const c of codes) {
    if (fullValue.startsWith(c)) return c;
  }
  return null;
}

function splitFullNumber(full?: string, defaultCode = "+91") {
  if (!full) return { code: defaultCode, local: "" };
  const code = findBestCountryCode(full) || defaultCode;
  return {
    code,
    local: full.startsWith(code) ? full.slice(code.length) : full,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────
export type PhoneNumberInputProps = {
  title?: string;
  value?: string;
  onChange?: (fullNumber: string) => void;
  defaultCode?: string;
  defaultCountryIso?: string;
  placeholder?: string;
  showFlag?: boolean;
};

// ─── Component ───────────────────────────────────────────────────────────────
export default function PhoneNumberInput({
  title = "Phone Number",
  value,
  onChange,
  defaultCode = "+966",
  defaultCountryIso,
  placeholder = "Enter your phone number",
  showFlag = true,
}: PhoneNumberInputProps) {

  const initialCountry =
    COUNTRIES.find(
      (c) => c.iso === defaultCountryIso
    ) ||
    COUNTRIES.find(
      (c) => c.code === defaultCode
    ) ||
    COUNTRIES[0];

  const [selectedCountryIso, setSelectedCountryIso] = useState(initialCountry.iso);
  const [selectedCode, setSelectedCode] = useState(initialCountry.code);
  const [localNumber, setLocalNumber] = useState("");

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [search, setSearch] = useState("");

  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!value) return;

    const parsed = splitFullNumber(
      value,
      defaultCode
    );

    const country =
      COUNTRIES.find(
        (c) => c.code === parsed.code
      );

    if (country) {
      setSelectedCountryIso(country.iso);
      setSelectedCode(country.code);
    }

    setLocalNumber(parsed.local);
  }, [value, defaultCode]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsDropdownOpen(false);
        setSearch("");
      }
    };
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  // Auto-focus search
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (isDropdownOpen) {
      timer = setTimeout(() => {
        searchRef.current?.focus();
      }, 50);
    }

    return () => clearTimeout(timer);
  }, [isDropdownOpen]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.includes(q) ||
        c.iso.toLowerCase().includes(q)
    );
  }, [search]);

  const emit = (newCode: string, newLocal: string) => {
    onChange?.(`${newCode}${newLocal}`);
  };

  const onLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "");
    setLocalNumber(raw);
    emit(selectedCode, raw);
  };

  const handleSelect = (
    c: {
      code: string;
      iso: string;
    }
  ) => {
    setSelectedCountryIso(c.iso);
    setSelectedCode(c.code);

    onChange?.(
      `${c.code}${localNumber}`
    );

    setIsDropdownOpen(false);
    setSearch("");
  };

  const selectedCountry = useMemo(() => {
    return (
      COUNTRIES.find(
        (c) => c.iso === selectedCountryIso
      ) ||
      COUNTRIES.find(
        (c) => c.code === selectedCode
      ) ||
      COUNTRIES[0]
    );
  }, [
    selectedCountryIso,
    selectedCode,
  ]);

  const isActive = isFocused || isDropdownOpen;

  return (
    <div className={styles.wrapper} ref={containerRef}>
      {/* ── Outer box ── */}
      <div className={`${styles.outerBox}${isActive ? ` ${styles.active}` : ""}`}>
        {/* Floating label */}

        <div className={styles.innerRow}>
          {/* Country selector */}
          <button
            type="button"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            className={styles.selectorBtn}
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
          >
            {showFlag && (
              <span className={styles.flag}>{selectedCountry.flag}</span>
            )}
            <span className={styles.codeText}>{selectedCode}</span>
          </button>

          {/* Divider */}
          <div className={styles.divider} />

          {/* Phone input */}
          <input
            type="tel"
            inputMode="numeric"
            placeholder={placeholder}
            value={localNumber}
            onChange={onLocalChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={styles.phoneInput}
            dir="ltr"
          />
        </div>
      </div>

      {/* ── Dropdown ── */}
      {isDropdownOpen && (
        <div ref={dropdownRef} className={styles.dropdown} role="listbox">
          {/* Search bar */}
          <div className={styles.searchWrap}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 20 20"
              fill="none"
              style={{ flexShrink: 0 }}
            >
              <circle cx="9" cy="9" r="6" stroke="#9ca3af" strokeWidth="2" />
              <path
                d="m16 16-3.5-3.5"
                stroke="#9ca3af"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <input
              ref={searchRef}
              type="text"
              placeholder="Search country or code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.searchInput}
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className={styles.clearBtn}
                type="button"
              >
                ✕
              </button>
            )}
          </div>

          {/* Country list */}
          <div className={styles.list}>
            {filtered.length === 0 ? (
              <div className={styles.noResults}>No countries found</div>
            ) : (
              filtered.map((item) => {
                const isSelected = selectedCode === item.code;
                return (
                  <div
                    key={item.iso}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(item)}
                    className={`${styles.listItem}${isSelected ? ` ${styles.selected}` : ""}`}
                  >
                    {showFlag && (
                      <span className={styles.itemFlag}>{item.flag}</span>
                    )}
                    <span className={styles.itemName}>{item.name}</span>
                    <span className={styles.itemIso}>{item.iso}</span>
                    <span className={styles.itemCode}>{item.code}</span>
                    {isSelected && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path
                          d="M2.5 7L5.5 10L11.5 4"
                          stroke="#6366f1"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}